import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  initialsFromName,
} from '@mlm/ui';
import type { ShowcaseRank } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { useRankAchievers } from '../../lib/hooks/use-ranks';
import { RankIcon } from './rank-icon';
import { rankDisplayName } from './rank-display';

// Static class strings (never interpolated, so Tailwind keeps them): a slight
// alternating tilt so the wall reads as pinned-up photos, not a table.
const TILTS = ['-rotate-2', 'rotate-1', '-rotate-1', 'rotate-2'] as const;

/**
 * Who holds a rank, as a hall-of-fame wall: big rank icon, name, description,
 * then gold-framed photo cards of the masked achiever names (the API masks
 * them; this never sees a full name), paginated.
 */
export function RankAchieversDialog({
  rank,
  onOpenChange,
}: {
  rank: ShowcaseRank | null;
  onOpenChange: (open: boolean) => void;
}) {
  const q = useRankAchievers(rank?.code ?? null);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Dialog open={!!rank} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        {rank && (
          <>
            <DialogHeader className="items-center text-center sm:text-center">
              <RankIcon
                key={rank.code}
                iconUrl={rank.iconUrl}
                index={rank.index}
                className="h-20 w-20"
              />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">
                Hall of Fame
              </p>
              <DialogTitle className="text-2xl capitalize">{rankDisplayName(rank)}</DialogTitle>
              <DialogDescription className="tabular-nums">
                {rank.achieverCount} {rank.achieverCount === 1 ? 'achiever' : 'achievers'}
              </DialogDescription>
            </DialogHeader>
            {rank.description && (
              <p className="whitespace-pre-line text-center text-sm text-muted-foreground">
                {rank.description}
              </p>
            )}
            <div className="mt-2">
              {q.isLoading ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-6 px-1 py-2 sm:grid-cols-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="aspect-[4/5] w-full" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No one has reached this rank yet.
                </p>
              ) : (
                <ul className="grid grid-cols-2 gap-x-4 gap-y-6 px-1 py-2 sm:grid-cols-3">
                  {items.map((a, i) => (
                    <li
                      key={`${a.name}-${a.achievedAt}-${i}`}
                      className={`rounded-sm bg-amber-200 p-2 pb-3 shadow-md ring-1 ring-amber-400/70 motion-safe:transition-transform motion-safe:duration-200 hover:rotate-0 dark:bg-amber-950/60 dark:ring-amber-700/60 ${TILTS[i % TILTS.length]}`}
                    >
                      <Avatar
                        src={a.avatarUrl}
                        initials={initialsFromName(a.name)}
                        className="aspect-square h-auto w-full rounded-none text-4xl"
                      />
                      <p className="mt-2 truncate text-center text-sm font-bold uppercase tracking-wide text-amber-950 dark:text-amber-50">
                        {a.name}
                      </p>
                      <p className="text-center text-[11px] tabular-nums text-amber-900/70 dark:text-amber-200/70">
                        Since {fmtDate(a.achievedAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {q.hasNextPage && (
                <div className="mt-4 flex justify-center">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={q.isFetchingNextPage}
                    onClick={() => void q.fetchNextPage()}
                  >
                    {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
